import { Toaster } from 'react-hot-toast';
import { Routes, Route, BrowserRouter } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import HomePage from './pages/HomePage';
import FestivalDetailPage from './pages/FestivalDetailPage';
import ChooseCreationMethodPage from './pages/ChooseCreationMethodPage';
import CreateFestivalPage from './pages/CreateFestivalPage';
import MyStandPage from './pages/MyStandPage';
import StandProfilePage from './pages/StandProfilePage';
import StandFestivalsPage from './pages/StandFestivalsPage';
import StandReferralPage from './pages/StandReferralPage';
import MyFestivalsPage from './pages/MyFestivalsPage';
import ManageFestivalDashboardPage from './pages/ManageFestivalDashboardPage';
import CreateFestivalStep2Page from './pages/CreateFestivalStep2Page';
import CreateFestivalBusinessModelPage from './pages/CreateFestivalBusinessModelPage';
import ExhibitorLandingPage from './pages/ExhibitorLandingPage';
import VisionPage from './pages/VisionPage';
import CreateFestivalExperiencePage from './pages/CreateFestivalExperiencePage';
import CreateFestivalPotentialPage from './pages/CreateFestivalPotentialPage';
import CreateFestivalStep3Page from './pages/CreateFestivalStep3Page';
import CreateFestivalStep4Page from './pages/CreateFestivalStep4Page';
import CreateFestivalDocsPage from './pages/CreateFestivalDocsPage';
import ReviewPage from './pages/ReviewPage';
import BecomePromoterPage from './pages/BecomePromoterPage';
import AdminPromotersPage from './pages/AdminPromotersPage';
import PromoterSignPage from './pages/PromoterSignPage';
import SubmitFestivalPage from './pages/SubmitFestivalPage';
import RegisterPage from './pages/RegisterPage';
import PromoterDashboardPage from './pages/PromoterDashboardPage';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import MobileNav from './components/MobileNav';
import Header from './components/Header';
import ScrollToTop from './components/ScrollToTop';
import { useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';

import ExploreStandsPage from './pages/ExploreStandsPage';
import StandDetailPage from './pages/StandDetailPage';
import SupportPage from './pages/SupportPage';
import SupportArticlePage from './pages/SupportArticlePage';
import ContactPage from './pages/ContactPage';
import MessagesPage from './pages/MessagesPage';
import NotificationsPage from './pages/NotificationsPage';
import HowItWorksPage from './pages/HowItWorksPage';
import VerifyEmail from './pages/VerifyEmail';
import UserSettings from './pages/UserSettings';
import AdminDashboardPage from './pages/AdminDashboardPage';

import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import TarificationPage from './pages/TarificationPage';
import AboutPage from './pages/AboutPage';
import { CookieBanner } from './components/CookieBanner';
import { OnboardingPanel } from './components/OnboardingPanel';
import { InstallPrompt } from './components/InstallPrompt';

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <Toaster position="bottom-right" toastOptions={{ style: { background: '#121212', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' } }} />
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const isLanding = location.pathname === '/' || location.pathname === '/exposants' || location.pathname === '/verify-email';
  const isCreation = location.pathname.includes('/create-festival') || location.pathname.includes('/choose-creation-method');
  const isManagement = location.pathname.includes('/manage-festival');
  const paddingClass = (isLanding || isCreation) ? '' : 'pt-16';

  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const handler = () => setOnboardingOpen(prev => !prev);
    window.addEventListener('open-onboarding', handler);
    return () => window.removeEventListener('open-onboarding', handler);
  }, []);

  // Parrainage exposant : capte `?ref=STAND-XXXX` sur n'importe quelle page et
  // le stocke en sessionStorage (lu par AuthModal à l'inscription d'un stand).
  // Param distinct du parrainage promoteur (`?referral=` / `festin_referral`).
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get('ref');
    if (ref && /^STAND-/i.test(ref)) {
      sessionStorage.setItem('festin_ref_stand', ref.toUpperCase());
    }
  }, [location.search]);

  // Auto-ouverture du guide de démarrage juste après l'inscription (une seule
  // fois). Flag posé par AuthModal à la création du compte, persistant en
  // localStorage pour survivre au détour /verify-email. Respecte la mémoire
  // projet : c'est le panel slide qui surgit, pas un banner top-page.
  useEffect(() => {
    if (user && localStorage.getItem('festin_just_signed_up') === '1') {
      localStorage.removeItem('festin_just_signed_up');
      const t = setTimeout(() => setOnboardingOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, [user]);

  return (
    <div className="min-h-screen pb-24 md:pb-0 relative overflow-hidden bg-[#0d0e12]">
      {/* Background Glow - High-impact vibrant orange gradient inspired by Shotgun app */}
      <div className="fixed -top-[20%] -right-[5%] w-[900px] h-[900px] rounded-full bg-gradient-to-br from-[#ff5a00]/40 via-[#ff5a00]/15 to-transparent blur-[160px] pointer-events-none z-0 opacity-90 animate-pulse" style={{ animationDuration: '8s' }} />
      <div className="fixed -top-[10%] right-0 w-[500px] h-[500px] rounded-full bg-[#ff5a00]/25 blur-[110px] pointer-events-none z-0" />
      <div className="fixed bottom-[10%] -left-[15%] w-[700px] h-[700px] rounded-full bg-[#ff5a00]/10 blur-[130px] pointer-events-none z-0" />
      
      <ScrollToTop />
      <CookieBanner />
      <InstallPrompt />
      {(!isLanding && !isCreation) && <Header />}
      <OnboardingPanel isOpen={onboardingOpen} onClose={() => setOnboardingOpen(false)} />
      <div className={`${paddingClass} relative z-10`}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/exposants" element={<ExhibitorLandingPage />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/vision" element={<VisionPage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/support/article/:slug" element={<SupportArticlePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/tarification" element={<TarificationPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/festival/:id" element={<FestivalDetailPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/review/:applicationId" element={<ReviewPage />} />
          <Route path="/settings" element={<UserSettings />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/become-promoter" element={<BecomePromoterPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/promoter/sign" element={<PromoterSignPage />} />
          <Route path="/promoter/submit-festival" element={<SubmitFestivalPage />} />
          <Route path="/promoter/dashboard" element={<PromoterDashboardPage />} />
          <Route element={<AdminRoute />}>
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/promoters" element={<AdminPromotersPage />} />
          </Route>

          {/* Routes réservées aux Festivals / Organisateurs */}
          <Route element={<ProtectedRoute allowedRole="festival" redirectTo="/stand/profile" />}>
            <Route path="/explore-stands" element={<ExploreStandsPage />} />
            <Route path="/stand-detail/:id" element={<StandDetailPage />} />
            <Route path="/choose-creation-method" element={<ChooseCreationMethodPage />} />
            <Route path="/create-festival" element={<CreateFestivalPage />} />
            <Route path="/create-festival/step-2" element={<CreateFestivalStep2Page />} />
            <Route path="/create-festival/step-experience" element={<CreateFestivalExperiencePage />} />
            <Route path="/create-festival/step-potential" element={<CreateFestivalPotentialPage />} />
            <Route path="/create-festival/step-3" element={<CreateFestivalBusinessModelPage />} />
            <Route path="/create-festival/step-4" element={<CreateFestivalStep3Page />} />
            <Route path="/create-festival/step-5" element={<CreateFestivalStep4Page />} />
            <Route path="/create-festival/step-docs" element={<CreateFestivalDocsPage />} />
            <Route path="/my-festivals" element={<MyFestivalsPage />} />
            <Route path="/manage-festival/:id" element={<ManageFestivalDashboardPage />} />
          </Route>

          {/* Routes réservées aux Stands */}
          <Route element={<ProtectedRoute allowedRole="stand" redirectTo="/my-festivals" />}>
            <Route path="/my-stand" element={<MyStandPage />} />
            <Route path="/stand/profile" element={<StandProfilePage />} />
            <Route path="/stand/festivals" element={<StandFestivalsPage />} />
            <Route path="/stand/parrainage" element={<StandReferralPage />} />
          </Route>

        </Routes>
      </div>
      <ConditionalMobileNav />
    </div>
  );
}

function ConditionalMobileNav() {
  const location = useLocation();
  const { user } = useAuth();
  if (location.pathname === '/' || location.pathname === '/exposants') return null;
  // Même correctif que Header.tsx : la bottom nav (Bord/Candidatures/Chat/
  // Profil...) n'a de sens que pour un compte connecté. Un visiteur anonyme
  // sur une page publique (/home, /festival/:id) n'a pas ces raccourcis.
  if (!user) return null;
  return <MobileNav />;
}
